import React, { useState } from 'react'
import { getEarningEstimates } from 'app/features/earn/earn.module'
import { claimRewards, Status } from 'app/features/earn/earnSlice'
import { useAppDispatch } from 'app/hooks/hooks'
import { classNames } from 'app/utils/tailwind'
import { secondsToDhms } from 'app/utils/web3'

import RebuyModal from './RebuyModal'

type NoEarningsProps = {
  apr?: number
}
const NoEarnings = (props: NoEarningsProps) => {
  const { apr = 0 } = props
  return (
    <div>
      <h3 className="mt-2 text-xl font-medium text-white">
        No rewards earned yet
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Earn upto <span className="font-bold text-gray-400">100% APR</span> by
        staking your XOPAD tokens
      </p>

      <div className="px-4 py-5 text-center text-white sm:px-6">
        <div>
          <dt className="text-sm font-medium text-gray-400 truncate">
            Current APR
          </dt>
          <dd className="mt-1 text-3xl font-semibold">{apr.toFixed(2)}%</dd>
        </div>
      </div>

      <div className="relative mt-5">
        <div className="relative flex justify-center animate-bounce">
          <span className="px-2 text-gray-700 bg-ebony-clay-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto mt-3 h-7 w-7 text-spring-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </div>
      </div>
    </div>
  )
}

type Props = {
  earned: number
  timeLeft: number
  canGetReward: boolean
  claimRewardStatus: Status
  stakedTokens: number
  stakedValue: number
  apr: number
}
const Earnings = (props: Props) => {
  const {
    earned,
    timeLeft,
    canGetReward,
    claimRewardStatus,
    stakedTokens,
    stakedValue,
    apr,
  } = props

  const dispatch = useAppDispatch()
  const [openModal, setOpenModal] = useState<boolean>(false)

  const showEarnings = stakedTokens

  const currentEarnings = () => {
    const handleClaimRewards = () => {
      dispatch(claimRewards())
        .unwrap()
        .then(() => {
          setOpenModal(true)
        })
        .catch((error) => {
          console.error(error)
          // TODO handle error
        })
    }

    const {
      day: currentDay,
      month: currentMonth,
      year: currentYear,
    } = getEarningEstimates(stakedValue, apr)

    return (
      <div>
        <dt className="text-sm font-medium text-gray-500 truncate">
          Current rewards
        </dt>
        <dd className="mt-1 text-3xl font-semibold text-white">
          ${earned.toFixed(4)}
        </dd>
        <div className="mt-5">
          {claimRewardStatus === Status.loading ? (
            <div className="flex items-center justify-center text-center align-middle">
              <svg
                className="animate-spin h-7 w-7 text-spring-green-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : (
            <button
              className={classNames(
                canGetReward
                  ? 'focus:ring-4 focus:outline-none focus:ring-spring-green-800 group-hover:from-spring-green-400 group-hover:to-cerulean-600 hover:text-white'
                  : 'cursor-not-allowed opacity-20',
                'relative inline-flex items-center justify-center p-0.5 mb-2 mr-2 overflow-hidden text-sm font-medium rounded-lg group bg-gradient-to-br from-spring-green-400 to-cerulean-600  text-white '
              )}
              disabled={!canGetReward}
              onClick={handleClaimRewards}
            >
              <span
                className={classNames(
                  canGetReward ? 'group-hover:bg-opacity-0' : '',
                  'relative px-5 py-2.5 transition-all ease-in duration-75 bg-ebony-clay-500 rounded-md '
                )}
              >
                Claim rewards
              </span>
            </button>
          )}

          {!canGetReward && (
            <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
              <dt className="text-sm font-medium text-gray-500 truncate">
                Time left until claimable
              </dt>
              <dd className="mt-1 font-semibold text-white text-md sm:text-lg md:text-2xl">
                {timeLeft && secondsToDhms(timeLeft)}
              </dd>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:px-10">
          <div className="mt-5 mb-5 text-center md:mt-10">
            <h3 className="text-sm font-medium text-gray-400 truncate">
              Estimated Earnings
            </h3>

            <dl className="grid grid-cols-3 gap-5">
              <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
                <dt className="text-sm font-medium text-gray-400 truncate">
                  Year
                </dt>
                <dd className="mt-1 text-xl font-semibold text-white sm:text-3xl">
                  ${currentYear.toFixed(0)}
                </dd>
              </div>

              <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
                <dt className="text-sm font-medium text-gray-400 truncate">
                  Month
                </dt>
                <dd className="mt-1 text-xl font-semibold text-white sm:text-3xl">
                  ${currentMonth.toFixed(0)}
                </dd>
              </div>

              <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
                <dt className="text-sm font-medium text-gray-400 truncate">
                  Day
                </dt>
                <dd className="mt-1 text-xl font-semibold text-white sm:text-3xl">
                  ${currentDay.toFixed(2)}
                </dd>
              </div>
            </dl>
          </div>

          <div className="px-4 py-5 text-center text-white sm:px-6">
            <div className="text-center">
              <div>
                <dl className="grid grid-cols-2 gap-5">
                  <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
                    <dt className="text-sm font-medium text-gray-400 truncate">
                      Your Staked Value
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-white sm:text-3xl">
                      ${stakedValue.toFixed(2)}
                    </dd>
                  </div>

                  <div className="px-1 py-1 overflow-hidden rounded-lg sm:p-2">
                    <dt className="text-sm font-medium text-gray-400 truncate">
                      Your Staked Tokens
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-white sm:text-3xl">
                      {stakedTokens.toFixed(0)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          <div className="mt-5 text-center text-white md:mt-10">
            <div>
              <dt className="text-sm font-medium text-gray-400 truncate">
                Current APR
              </dt>
              <dd className="mt-1 text-3xl font-semibold">{apr.toFixed(2)}%</dd>
            </div>
          </div>
        </div>
        <RebuyModal {...{ openModal, setOpenModal }} />
      </div>
    )
  }

  return (
    <>
      <div className="mb-10 text-center">
        <svg
          className="w-12 h-12 mx-auto text-gray-400"
          fill="currentColor"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
            clipRule="evenodd"
          />
        </svg>
        {showEarnings ? currentEarnings() : NoEarnings({ apr })}
      </div>
    </>
  )
}

export default Earnings
